import { TableFilterConfig } from '@/components/data-table/filters-v2';
import i18n from '@/core/i18n';
import { DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES } from '@/lib/constants/pagination';
import { EntityAvatar, getTeamMemberStatusOptions } from '@/modules/common';
import { CustomAttribute } from '@/modules/common/types/custom-attribute';
import { buildCustomAttributeFilterConfigsV2 } from '@/modules/common/utils/custom-attribute-filters';
import { MembershipType } from '@/modules/team/types/membership-type';
import { FilterConfig, FilterOption, RangeMode } from '@/types/filters';

import {
  loadCitiesForFilter,
  loadCountriesForFilter,
  loadDepartmentsForFilter,
  loadRolesForFilter,
  loadSenioritiesForFilter,
} from '../api/team-filter-options.api';
import { getShortTeamMembers } from '../api/team.api';
import { MemberStatus } from '../types';
import { NON_ARCHIVED_TEAM_MEMBER_STATUSES } from '../types/dto/find-short-team-members.request';
import {
  TEAM_FILTER_KEY_CAPACITY,
  TEAM_FILTER_KEY_CITY,
  TEAM_FILTER_KEY_COUNTRY,
  TEAM_FILTER_KEY_DEPARTMENT,
  TEAM_FILTER_KEY_IS_MANAGER,
  TEAM_FILTER_KEY_MANAGER,
  TEAM_FILTER_KEY_MEMBERSHIP_TYPE,
  TEAM_FILTER_KEY_ROLE,
  TEAM_FILTER_KEY_SEARCH,
  TEAM_FILTER_KEY_SENIORITY,
  TEAM_FILTER_KEY_STATUS,
} from './team-filter-keys';

export const getTeamFiltersConfig = (customAttributes?: CustomAttribute[]): TableFilterConfig[] => [
  {
    type: 'autocomplete',
    name: TEAM_FILTER_KEY_STATUS,
    label: i18n.t('authenticated.team:status.label'),
    options: getTeamMemberStatusOptions([
      MemberStatus.Active,
      MemberStatus.Invited,
      MemberStatus.Expired,
      MemberStatus.Basic,
      MemberStatus.Placeholder,
      MemberStatus.Archived,
    ]),
  },
  {
    type: 'search',
    name: TEAM_FILTER_KEY_SEARCH,
    label: i18n.t('common:search'),
    placeholder: i18n.t('authenticated.team:searchPlaceholder'),
  },
  {
    type: 'dynamic-autocomplete',
    name: TEAM_FILTER_KEY_DEPARTMENT,
    label: i18n.t('authenticated.team:department'),
    withSearch: true,
    loadOptions: loadDepartmentsForFilter,
  },
  {
    type: 'dynamic-autocomplete',
    name: TEAM_FILTER_KEY_SENIORITY,
    label: i18n.t('authenticated.team:seniority.label'),
    withSearch: true,
    loadOptions: loadSenioritiesForFilter,
  },
  {
    type: 'dynamic-autocomplete',
    name: TEAM_FILTER_KEY_ROLE,
    label: i18n.t('authenticated.team:role'),
    withSearch: true,
    loadOptions: loadRolesForFilter,
  },
  {
    type: 'dynamic-autocomplete',
    name: TEAM_FILTER_KEY_MANAGER,
    label: i18n.t('authenticated.team:manager'),
    withSearch: true,
    loadOptions: async params =>
      getShortTeamMembers({
        filter: {
          isManager: true,
          searchString: params.searchString,
          statuses: NON_ARCHIVED_TEAM_MEMBER_STATUSES,
        },
        pagination: { cursor: params.cursor, pageSize: DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES },
      }),
    renderLabel: (value: string, option: FilterOption) => (
      <div className="flex items-center gap-2">
        <EntityAvatar image={option.avatar} name={value} size="xs" shape="circle" />
        <span className="text-sm">{value}</span>
      </div>
    ),
    minSearchChars: 2,
    debounceMs: 250,
  },
  {
    type: 'rangeMode',
    name: TEAM_FILTER_KEY_CAPACITY,
    label: i18n.t('authenticated.team:capacity'),
    placeholder: i18n.t('authenticated.team:filters.example'),
    modes: [
      {
        label: i18n.t('authenticated.team:filters.moreThan'),
        value: RangeMode.More,
      },
      {
        label: i18n.t('authenticated.team:filters.lessThan'),
        value: RangeMode.Less,
      },
      {
        label: i18n.t('authenticated.team:filters.equalTo'),
        value: RangeMode.Equal,
      },
    ],
    unit: i18n.t('authenticated.team:filters.unit'),
  },
  {
    type: 'autocomplete',
    name: TEAM_FILTER_KEY_MEMBERSHIP_TYPE,
    label: i18n.t('authenticated.team:membershipType'),
    options: [
      {
        value: MembershipType.InHouse,
        label: i18n.t('authenticated.team:membershipTypes.inHouse'),
      },
      {
        value: MembershipType.Partner,
        label: i18n.t('authenticated.team:membershipTypes.partner'),
      },
      {
        value: MembershipType.Contractor,
        label: i18n.t('authenticated.team:membershipTypes.contractor'),
      },
    ],
  },
  {
    type: 'dynamic-autocomplete',
    name: TEAM_FILTER_KEY_COUNTRY,
    withSearch: true,
    label: i18n.t('authenticated.team:country'),
    loadOptions: loadCountriesForFilter,
  },
  {
    type: 'dynamic-autocomplete',
    name: TEAM_FILTER_KEY_CITY,
    withSearch: true,
    label: i18n.t('authenticated.team:city'),
    loadOptions: loadCitiesForFilter,
    dependsOn: TEAM_FILTER_KEY_COUNTRY,
  },
  {
    type: 'switch',
    name: TEAM_FILTER_KEY_IS_MANAGER,
    label: i18n.t('authenticated.team:filters.isManager'),
  },
  ...(customAttributes
    ? buildCustomAttributeFilterConfigsV2(customAttributes, 10, {
        noValueOptionLabel: i18n.t('common:tableFilters.noAttributeValue'),
      })
    : []),
];

export const getTeamMemberChangesTabFiltersConfig = (): FilterConfig[] => [
  {
    type: 'autocomplete',
    name: 'changeType',
    label: i18n.t('authenticated.team:details.tabs.changes.filters.changeType'),
    options: [
      {
        label: i18n.t('authenticated.team:details.tabs.changes.info'),
        value: 'info',
      },
      {
        label: i18n.t('authenticated.team:details.tabs.changes.billing'),
        value: 'billing',
      },
      {
        label: i18n.t('authenticated.team:details.tabs.changes.capacity'),
        value: 'capacity',
      },
      {
        label: i18n.t('authenticated.team:details.tabs.changes.integration'),
        value: 'integration',
      },
    ],
  },
  {
    type: 'dateRange',
    name: 'dateRange',
    label: i18n.t('authenticated.team:details.tabs.changes.filters.dateRange'),
  },
  {
    type: 'dynamic-autocomplete-paginated',
    name: 'changedBy',
    label: i18n.t('authenticated.team:details.tabs.changes.filters.changedBy'),
    withSearch: true,
    loadItems: async (params: {
      searchString?: string;
      cursor?: number | null;
      [key: string]: any;
    }) => {
      const response = await getShortTeamMembers({
        filter: params.searchString ? { searchString: params.searchString } : undefined,
        pagination: {
          pageSize: DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES,
          cursor: params.cursor,
        },
      });
      return {
        items: response.items.map(item => ({
          id: item.id,
          name: item.name,
          avatarLink: item.avatarLink,
        })),
        pagination: response.pagination,
      };
    },
    CustomLabelComponent: (value: string, opt: FilterOption) => (
      <div className="flex items-center gap-2">
        <EntityAvatar name={value} image={opt.avatar ?? undefined} size="xs" shape="circle" />
        <span className="text-sm">{value}</span>
      </div>
    ),
  },
];
